import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {TranslateService} from '../_services/translate.service';
import {catchError, finalize, map} from 'rxjs/operators';
import Swal from 'sweetalert2';
import {environment} from '@environments/environment';
import {Auth, IS_TOOL, SKIP_AUTHENTICATION, SKIP_MODAL_ERROR} from '../_models/auth';
import {AuthenticationService} from '@app/shared/_services/authentication.service';
import {JwtSwal} from '@app/shared/_interfaces/jwt';
import {Router} from '@angular/router';
import {CookieService} from 'ngx-cookie-service';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private _authenticationService = inject(AuthenticationService);
  private trans = inject(TranslateService);
  private router = inject(Router);
  private cookieService = inject(CookieService);


  swal(data: JwtSwal) {
    if (data.type === 'swal') {
      return Swal.fire(data.params.title, data.params.message, data.params.severity);
    }
    return new Promise((resolve, reject) => resolve);
  }

  private skipModalError(req: HttpRequest<any>, status: number | 'all') {
    return req.context.get(SKIP_MODAL_ERROR)
      .some(_status => status === _status);
  }

  private isTool(req: HttpRequest<any>) {
    return req.context.get(IS_TOOL) !== false;
  }

  private getTool(req: HttpRequest<any>) {
    return req.context.get(IS_TOOL);
  }

  private getErrorMessage = (title: string, err: HttpErrorResponse) => {

    const translatedTitle = this.trans.translate(title);

    if (environment.production) {
      return translatedTitle;
    }

    return `
        <p><strong>${translatedTitle}</strong></p>
        <p><strong>${this.trans.translate('jwt_error_url')}</strong>: ${err.url}</p>
        <p><strong>${this.trans.translate('jwt_error_status')}</strong>: ${err.status}</p>
        <p><strong>${this.trans.translate('jwt_error_full_details')}</strong>:</p>
        <p><textarea style="width:100%" rows="10" readonly>${JSON.stringify(err)}</textarea></p>
    `;
  };

  private isApisixError(err: HttpErrorResponse): boolean {
    // Check Content-Type header for HTML response
    const contentType = err.headers.get('content-type');

    if (contentType && contentType.includes('text/html')) {
      return true;
    }

    // Check if error body is a string (HTML response from APISIX)
    if (typeof err.error === 'string') {
      return true;
    }

    // Check if error is null/undefined but status is 401 (typical APISIX behavior)
    if (err.status === 401 && (err.error === null || err.error === undefined)) {
      return true;
    }

    return false;
  }



  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    // Clone the request and add credentials to include cookies
    req = req.clone({
      withCredentials: true
    });

    if (!req.context.get(SKIP_AUTHENTICATION)) {

      const user: Auth = this._authenticationService.currentUserValue;
      if (user.authkey != null) {

        let authkey = user.authkey;

        req = req.clone({
          setHeaders: {
            'Authorization': authkey
          }
        });
      }
    }

    // Check for targetAccountId in cookies and add it to headers
    const targetAccountId = this.cookieService.get('targetAccountId');
    if (targetAccountId) {
      req = req.clone({
        setHeaders: {
          'x-target-accountid': targetAccountId
        }
      });
    }

    return next.handle(req).pipe(
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          // Reset 401 error count on successful response
          localStorage.setItem('http_error_401_count', '0');

          if (
            event.body &&
            event.body.status &&
            ['error', 'ERROR'].includes(event.body.status)) {
            throw new HttpErrorResponse({error: event.body, status: 400});
          }
          if (
            this.isTool(req)
          ) {
            if (event.body && (
              ('enabled' in event.body && 'is_pro' in event.body && !event.body.enabled && !event.body.is_pro) ||
              ('executive_plan' in event.body && event.body.executive_plan))
            ) {

              const disabled = ~req.url.indexOf('/disable');

              throw new HttpErrorResponse({
                error:
                  {
                    message: this.trans.switchTranslate(
                      `settings_setting_${this.getTool(req)}_${disabled ? 'notdisabled' : 'notenabled'}`,
                      `settings_setting_${disabled ? 'notdisabled' : 'notenabled'}`,
                      {
                        service: this.getTool(req)
                      }
                    ),
                  },
                status: 403
              });
            }
          }
        }
        return event;
      }),
      catchError(err => {
        const type = this.isTool(req) ? 'cored' : 'swal';

        if (err instanceof HttpErrorResponse && !this.skipModalError(req, 'all')) {

          if (err.error?.error && err.error?.error?.message) {
            err.error.message = err.error.error.message;
          }

          if (err.error?.msg) {
            err.error.message = err.error.msg;
          }

          if (err.error.message === 'invalid_mail_address_list'){
            err.error.message = 'emailvalidator_slide_description';
          }

          const {message} = err.error as { message: string, status: 'error' };

          if (!this.skipModalError(req, err.status)) {
            switch (err.status) {
              case 401: {
                if (this.isApisixError(err)) {
                  console.log("APISIX 401 - redirect to loginUrl");
                  this._authenticationService.deauthorizeToken();
                  window.location.href = environment.loginUrl;
                } else {
                  const RETRY_LIMIT = 5;
                  const STORAGE_KEY = 'http_error_401_count';
                  let count = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
                  count++;

                  if (count > RETRY_LIMIT) {
                    // Backend 401 Limit Reached
                    localStorage.setItem(STORAGE_KEY, '0');
                    console.log("Backend 401 Limit Reached - Redirecting to server error page");
                    this.router.navigate(['/server-error']);
                  } else {
                    localStorage.setItem(STORAGE_KEY, count.toString());
                    console.log(`Backend 401 encountered (Count: ${count}). Propagating error.`);
                    // Do not redirect to loginUrl to avoid infinite loops for normal users
                  }
                }
                break;
              }
              case 400:
                this.swal({
                  type,
                  params: {
                    title: this.trans.translate('error') + ' :(',
                    'message': this.getErrorMessage(message, err),
                    severity: 'error'
                  }
                });
                break;
              case 404:
                if (message) {
                  this.swal({
                    type,
                    params: {
                      title: this.trans.translate('error') + ' :(',
                      message: this.trans.translate(message),
                      severity: 'error'
                    }
                  });
                } else {
                  if (!environment.production) {
                    this.swal({
                      type,
                      params: {
                        title: this.trans.translate('error') + ' :(',
                        'message': this.getErrorMessage('jwt_url_not_exists', err),
                        severity: 'error'
                      }
                    });
                  }
                }
                break;
              case 413:
                this.swal({
                  type,
                  params: {
                    title: this.trans.translate('error') + ' :(',
                    'message': this.getErrorMessage('file_too_large', err),
                    severity: 'error'
                  }
                });
                break;
              case  500:
                this.swal({
                  type,
                  params: {
                    title: this.trans.translate('error') + ' :(',
                    message: this.getErrorMessage('server_problem_please_retry', err),
                    severity: 'error'
                  }
                });
                break;
              case 504:
                this.swal({
                  type,
                  params: {
                    title: this.trans.translate('error') + ' :(',
                    message: this.getErrorMessage('server_problem_gateway_timeout', err),
                    severity: 'error'
                  }
                });
                break;
              case 0:
                this.swal({
                  type,
                  params: {
                    title: this.trans.translate('error') + ' :(',
                    message: this.getErrorMessage('server_problem_unknown_error', err),
                    severity: 'error'
                  }
                });
                break;
              default:
                this.swal({
                  type,
                  params: {
                    title: this.trans.translate('error') + ' :(',
                    'message': this.trans.translate(err.error?.message),
                    severity: 'error'
                  }
                });
                break;
            }
          }
        }
        return throwError(err);
      }),
      finalize(() => {
      }),
    );

  }
}
